// =========================================================================
// The flow — the seven steps the operator executes, start to end, with live
// status and the button for each (docs/WALLET-FLOW-BUILD-2026-10-17.md).
//
//   1 Register the Ledger      2 Issue an address per deal    3 Customer pays
//   4 Sweep (sign by cable)    5 Payout address               6 Pay
//   7 Treasury
//
// Status comes from the same reads the other tabs make (/me, /keys, /summary,
// /approvals, /whitelist, the deposit addresses, the window) — this screen
// adds no facts of its own, it lines the existing ones up in the order the
// money moves. The approval window sits at the top because steps 4, 6 and 7
// are refused without one.
//
// On the design system (22 Oct 2026): every status is ONE line — what stands
// and what to do; the explanation of each step is behind "About".
// Screens' round 2 (23 Oct 2026): a failed read says "try again" with a
// button that reads again, on every step and block.
// =========================================================================

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, CircleDot } from 'lucide-react';
import { custody as api, type RenewalRow, type FeeFloorClass, type MoveLimits } from '../../lib/api';
import { FEE_FLOOR_CLASSES } from '../../../../contracts/wallet.mjs';
import { Card, StateLine, Pending, MoreDetails, Address } from '../../components/kit';
import { limitsChanged, limitsRefusal } from './refusals';
import { CLASS_WORD, money, relativeTime, groupDigits } from '../../lib/custody-format';
import { renewalLine, renewConfirm, renewalRefusal, everyDaysProblem, RENEWAL_DAYS_MIN, RENEWAL_DAYS_MAX } from '../../lib/custody-renewals';
import { useRenewNow } from '../../lib/renew-watch';
import { payStepStatus, whitelistGroup } from '../../lib/custody-screen';
import { webHid } from '../../lib/ledger-cable';
import { WindowControl } from './MoveSheet';
import { ReadFailed } from './ReadFailed';
import { isCustodySignin } from '../../lib/errors';
import { FIELD } from './field';

type Tone = 'done' | 'now' | 'todo';

const textOf = (err: unknown): string => String((err as { message?: unknown } | null)?.message ?? err);

/** A read failing only on the lapsed custody sign-in: the section's one line says it (CustodySigninShell); a step waits. */
const signedOut = (q: { error: unknown }) => isCustodySignin(q.error);

function Step({ n, title, tone, status, bad, retry, reading = false, more, to, action }: {
  n: number;
  title: string;
  tone: Tone;
  /** One line on where this step stands. */
  status: string;
  /** Set when the line is a failed read: the server's words sit behind Details. */
  bad?: string | null;
  /** Reads again what failed — the line's one action while `bad` is set. */
  retry?: () => void;
  /** True while that read is running again. */
  reading?: boolean;
  /** The explanation, behind a tap. */
  more?: string;
  to?: string;
  action?: string;
}) {
  const Icon = tone === 'done' ? CheckCircle2 : tone === 'now' ? CircleDot : Circle;
  const iconTone = tone === 'done' ? 'text-pos' : tone === 'now' ? 'text-tg-accent lg:text-brand-gold' : 'text-tg-hint';
  return (
    <Card className="flex items-start gap-3">
      <Icon className={`mt-1 h-4 w-4 shrink-0 ${iconTone}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-meta text-tg-hint">{n}</span>
          <h3 className="text-body font-semibold">{title}</h3>
        </div>
        <StateLine
          className="mt-1"
          tone={bad ? 'bad' : tone === 'done' ? 'good' : 'neutral'}
          more={bad ?? more}
          action={bad && retry ? <button type="button" className="btn-secondary min-h-[44px] text-body lg:min-h-[40px]" disabled={reading} onClick={retry}>{reading ? 'Reading…' : 'Try again'}</button> : undefined}
        >
          {status}
        </StateLine>
      </div>
      {to && action && (
        <Link to={to} className="btn-secondary min-h-[44px] lg:min-h-[40px] shrink-0 text-body">{action}</Link>
      )}
    </Card>
  );
}

// ---- renewals (slice E, 18 Oct 2026) ----------------------------------------
//
// The three floats renew on the operator's intervals (17 Oct: Merchant Hot
// every 2–3 days, Hot every 15, Treasury every 30). One line per class says
// when, and from which Ledger account; "every N days" sets the interval,
// "Renew now" makes it due so the desk's next tick issues the next address.
// The old address is retired — still watched, still read by the Books — and
// its balance moves only on the operator's Ledger (18 Oct), as a card in
// Approvals. Nothing on this block moves money.

function RenewalLine({ r }: { r: RenewalRow }) {
  const qc = useQueryClient();
  const [days, setDays] = useState(String(r.everyDays));
  useEffect(() => { setDays(String(r.everyDays)); }, [r.everyDays]);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['custody', 'renewals'] });
    void qc.invalidateQueries({ queryKey: ['custody', 'balances'] });
  };
  const setEvery = useMutation({ mutationFn: (n: number) => api.setRenewalDays(r.class, n), onSuccess: refresh });
  // Renew now, followed until the row shows the new address (lib/renew-watch.ts — his live test, 3 Nov 2026).
  const renewal = useRenewNow(r);
  const problem = days.trim() === String(r.everyDays) ? null : everyDaysProblem(days);
  function save() {
    const t = days.trim();
    if (t === '' || everyDaysProblem(t) !== null) {
      setDays(String(r.everyDays));
      return;
    }
    const n = Number(t);
    if (n === r.everyDays) return;
    setEvery.mutate(n);
  }
  function renew() {
    if (!window.confirm(renewConfirm(r.class))) return;
    renewal.renew();
  }
  return (
    // Stacked on a phone — the line, then the controls — side by side from sm up:
    // squeezed beside a fixed-width control group, the line wrapped one word a row (render of 22 Oct 2026).
    <div className="row flex-col items-stretch gap-2 sm:flex-row sm:items-start">
      <span className="min-w-0 text-body">
        <span>{renewalLine(r)}</span>
        {!r.keyRegistered && (
          <Link to="/custody/keys" className="ml-1.5 text-meta text-tg-link underline">Devices &amp; keys</Link>
        )}
        {r.active && (
          <span className="mt-0.5 block text-meta">
            <Address value={r.active.address} />
          </span>
        )}
        {problem && <StateLine tone="bad" className="mt-0.5">{problem}</StateLine>}
        {setEvery.isError && <StateLine tone="bad" className="mt-0.5" more={renewalRefusal(setEvery.error)}>Not saved.</StateLine>}
        {setEvery.isSuccess && !problem && <StateLine tone="good" className="mt-0.5">Saved: every {r.everyDays} days, from the next renewal.</StateLine>}
        {renewal.stage === 'failed' && <StateLine tone="bad" className="mt-0.5" more={renewalRefusal(renewal.error)}>Not renewed.</StateLine>}
        {renewal.stage === 'waiting' && <StateLine className="mt-0.5">Renewing — waiting for the new address.</StateLine>}
        {renewal.stage === 'renewed' && <StateLine tone="good" className="mt-0.5" more="The old address is retired and still watched; the move of its balance appears in Approvals for your Ledger.">Renewed — the new address is in use.</StateLine>}
        {renewal.stage === 'late' && <StateLine tone="warn" className="mt-0.5" more="Still due: the desk renews it on a later tick, and this line shows the new address when it does.">Not renewed yet.</StateLine>}
      </span>
      <span className="flex flex-wrap items-center gap-1.5 text-body sm:shrink-0">
        <label className="flex items-center gap-1 text-tg-hint">
          every
          <input
            className={`${FIELD} w-14 text-right font-mono`}
            inputMode="numeric"
            min={RENEWAL_DAYS_MIN}
            max={RENEWAL_DAYS_MAX}
            value={days}
            disabled={setEvery.isPending}
            aria-label={`${CLASS_WORD[r.class] ?? r.class}: renew every how many days`}
            onChange={(e) => setDays(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          />
          days
        </label>
        <button
          className="btn-secondary min-h-[44px] lg:min-h-[40px] text-body"
          disabled={!r.keyRegistered || renewal.stage === 'marking' || renewal.stage === 'waiting'}
          title={r.keyRegistered ? undefined : 'Register this class\'s account key first — without it the desk cannot issue the next address.'}
          onClick={renew}
        >
          {renewal.stage === 'marking' ? 'Marking…' : renewal.stage === 'waiting' ? 'Renewing…' : 'Renew now'}
        </button>
      </span>
    </div>
  );
}

function Renewals() {
  const q = useQuery({ queryKey: ['custody', 'renewals'], queryFn: api.renewals, refetchInterval: 30_000, retry: false });
  return (
    <Card title="Renewals">
      {q.isError ? (
        <ReadFailed what="the renewals" error={q.error} onRetry={() => void q.refetch()} pending={q.isFetching} />
      ) : !q.data ? (
        <Pending inline what="Reading…" />
      ) : q.data.renewals.length === 0 ? (
        <StateLine>No renewable class yet.</StateLine>
      ) : (
        q.data.renewals.map((r) => <RenewalLine key={r.class} r={r} />)
      )}
    </Card>
  );
}

/**
 * The move caps (21 Oct 2026, risk 13): per move, per Dubai day, the first
 * payout to a new address, what today already carries — and the two-device
 * switch (db/0088): off, one operator device signs and sends a whitelisted
 * payout (the operator, 22 Oct 2026); on, a payout waits for the other
 * registered device's approval. One row, read by the dashboard and the desk.
 */
/**
 * The TRX each float keeps for its own chain fees (db/0097; the operator's
 * decision 1, 23 Oct 2026): the desk refills a float from the fee wallet under
 * half its floor, and refuses a new payout from a source under 15 TRX before
 * the Nano. HIS figures — the seeds stand until he sets them, and the page
 * says so.
 */
export function FeeFloors() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['custody', 'fee-floors'], queryFn: api.feeFloors, refetchInterval: 60_000, retry: false });
  // Every float with a floor, Flagged included (fee floors round 1, #7): the list is the contract's, not this card's.
  const classes = FEE_FLOOR_CLASSES as readonly FeeFloorClass[];
  const [draft, setDraft] = useState<Partial<Record<FeeFloorClass, string>>>({});
  const read = classes.map((cls) => q.data?.floors[cls] ?? '').join('|');
  useEffect(() => { if (q.data) setDraft({ ...q.data.floors }); }, [read]);   // eslint-disable-line react-hooks/exhaustive-deps
  const save = useMutation({
    mutationFn: () => api.setFeeFloors(Object.fromEntries(classes.map((cls) => [cls, (draft[cls] ?? '').trim()])) as Record<FeeFloorClass, string>),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['custody', 'fee-floors'] }); },
  });
  const changed = q.data !== undefined && classes.some((cls) => (draft[cls] ?? '').trim() !== (q.data!.floors[cls] ?? ''));
  const field = (cls: FeeFloorClass) => (
    <input
      className={`${FIELD} w-20 text-right font-mono`}
      inputMode="decimal"
      value={draft[cls] ?? ''}
      disabled={save.isPending || q.isError}
      aria-label={`${CLASS_WORD[cls]} floor, TRX`}
      onChange={(e) => setDraft({ ...draft, [cls]: e.target.value })}
      onKeyDown={(e) => { if (e.key === 'Enter' && changed) save.mutate(); }}
    />
  );
  return (
    <Card title="Fee floors">
      {q.isError ? (
        <ReadFailed what="the fee floors" error={isCustodySignin(q.error) ? q.error : limitsRefusal(q.error)} onRetry={() => void q.refetch()} pending={q.isFetching} />
      ) : !q.data ? (
        <Pending inline what="Reading…" />
      ) : (
        <div className="row flex-col items-stretch gap-2 sm:flex-row sm:items-start">
          <span className="min-w-0 text-body">
            <span>{classes.map((cls) => `${CLASS_WORD[cls]} ${q.data!.floors[cls] ?? '—'} TRX`).join(' · ')}</span>
            <span className="mt-0.5 block text-meta text-tg-hint">
              {q.data.set ? `Set ${q.data.updatedAt ? relativeTime(q.data.updatedAt) : ''} by ${q.data.updatedBy ?? '—'}` : 'Defaults until you set them'} · refilled under half or {q.data.minTrx}
            </span>
            {save.isError && <StateLine tone="bad" className="mt-0.5" more={limitsRefusal(save.error)}>Not saved.</StateLine>}
            {save.isSuccess && !changed && <StateLine tone="good" className="mt-0.5">Saved.</StateLine>}
          </span>
          <span className="flex flex-wrap items-center gap-1.5 text-body sm:shrink-0">
            {classes.map((cls) => (
              <label key={cls} className="flex items-center gap-1 text-tg-hint">{CLASS_WORD[cls]} {field(cls)}</label>
            ))}
            <button className="btn-secondary min-h-[44px] text-body lg:min-h-[40px]" disabled={!changed || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save'}</button>
          </span>
        </div>
      )}
      <MoreDetails label="About fee floors">
        <p className="text-meta text-tg-hint">A wallet pays its own chain fee in TRX when the energy desk cannot: one USDT transfer burns about 6.5 TRX to an address that holds USDT and 13 to one that holds none. The desk reads each wallet's TRX every three minutes and refills it from Fee when it falls under half its floor, or under the {q.data?.minTrx ?? '15'} TRX a payout needs — at most three refills an hour per wallet, each up to 50 TRX. Before a sweep that will burn, it tops the source up for that sweep and any other still sending from it.</p>
        <p className="text-meta text-tg-hint">A new payout is refused before your Ledger when its source holds under {q.data?.minTrx ?? '15'} TRX at the last reading; the seeded figures (Hot 100, Merchant Hot 50, Flagged 20, Treasury 20) stand until you save your own here.</p>
      </MoreDetails>
    </Card>
  );
}

function Caps() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['custody', 'limits'], queryFn: api.limits, refetchInterval: 30_000, retry: false });
  const [perMove, setPerMove] = useState('');
  const [perDay, setPerDay] = useState('');
  // No caps (db/0098): unticked while testing — the caps are enforced; ticked on his word.
  const [capsOff, setCapsOff] = useState(false);
  const [twoDevices, setTwoDevices] = useState(false);
  // The two sources that pay merchants only (db/0089), each within Hot's caps.
  const [merchantMove, setMerchantMove] = useState('');
  const [merchantDay, setMerchantDay] = useState('');
  const [flaggedMove, setFlaggedMove] = useState('');
  const [flaggedDay, setFlaggedDay] = useState('');
  const src = q.data?.sources;
  // THE VERSION THE FIELDS WERE FILLED FROM (P-060): sent as ifUpdatedAt. A save that came in after it refuses this one (409
  // limits_changed, nothing written): his figures stay in the fields, the caps as they stand are said under them, and Save goes
  // again against those. While that conflict stands, a refresh does not refill the fields over what he typed.
  const [seededAt, setSeededAt] = useState<string | null>(null);
  const [conflict, setConflict] = useState<MoveLimits | null>(null);
  useEffect(() => {
    if (q.data && !conflict) {
      setSeededAt(q.data.updatedAt ?? null);
      setPerMove(q.data.moveCeilingUsdt); setPerDay(q.data.dayCeilingUsdt); setCapsOff(q.data.capsOff === true); setTwoDevices(q.data.secondDevicePayouts === true);
      setMerchantMove(q.data.sources?.dirty_hot?.moveCeilingUsdt ?? ''); setMerchantDay(q.data.sources?.dirty_hot?.dayCeilingUsdt ?? '');
      setFlaggedMove(q.data.sources?.frozen?.moveCeilingUsdt ?? ''); setFlaggedDay(q.data.sources?.frozen?.dayCeilingUsdt ?? '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conflict, q.data?.moveCeilingUsdt, q.data?.dayCeilingUsdt, q.data?.capsOff, q.data?.secondDevicePayouts, src?.dirty_hot?.moveCeilingUsdt, src?.dirty_hot?.dayCeilingUsdt, src?.frozen?.moveCeilingUsdt, src?.frozen?.dayCeilingUsdt]);
  const sourcesChanged = src !== undefined && (
    merchantMove.trim() !== src.dirty_hot?.moveCeilingUsdt || merchantDay.trim() !== src.dirty_hot?.dayCeilingUsdt
    || flaggedMove.trim() !== src.frozen?.moveCeilingUsdt || flaggedDay.trim() !== src.frozen?.dayCeilingUsdt);
  const save = useMutation({
    mutationFn: () => api.setLimits({
      moveCeilingUsdt: perMove.trim(), dayCeilingUsdt: perDay.trim(), capsOff, secondDevicePayouts: twoDevices,
      ...(src ? { sources: { dirty_hot: { moveCeilingUsdt: merchantMove.trim(), dayCeilingUsdt: merchantDay.trim() }, frozen: { moveCeilingUsdt: flaggedMove.trim(), dayCeilingUsdt: flaggedDay.trim() } } } : {}),
      ifUpdatedAt: conflict ? conflict.updatedAt ?? null : seededAt,
    }),
    onError: (e) => {
      const now = limitsChanged(e) ? (e.body as Record<string, unknown>).current : undefined;
      if (now && typeof now === 'object') setConflict(now as MoveLimits);
    },
    onSuccess: () => {
      setConflict(null);
      void qc.invalidateQueries({ queryKey: ['custody', 'limits'] });
      void qc.invalidateQueries({ queryKey: ['custody', 'whitelist'] });
      void qc.invalidateQueries({ queryKey: ['custody', 'approvals'] });
    },
  });
  const changed = q.data !== undefined && (perMove.trim() !== q.data.moveCeilingUsdt || perDay.trim() !== q.data.dayCeilingUsdt || capsOff !== (q.data.capsOff === true) || twoDevices !== (q.data.secondDevicePayouts === true) || sourcesChanged);
  const field = (value: string, set: (v: string) => void, label: string) => (
    <input
      className={`${FIELD} w-24 text-right font-mono`}
      inputMode="decimal"
      value={value}
      disabled={save.isPending || q.isError}
      aria-label={label}
      onChange={(e) => set(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter' && changed) save.mutate(); }}
    />
  );
  return (
    <Card title="Caps">
      {q.isError ? (
        <ReadFailed what="the caps" error={isCustodySignin(q.error) ? q.error : limitsRefusal(q.error)} onRetry={() => void q.refetch()} pending={q.isFetching} />
      ) : !q.data ? (
        <Pending inline what="Reading…" />
      ) : (
        <div className="row flex-col items-stretch gap-2 sm:flex-row sm:items-start">
          <span className="min-w-0 text-body">
            <span>{q.data.capsOff ? 'No caps' : <>Per move {money(q.data.moveCeilingUsdt, 'USDTTRC')} · per day {money(q.data.dayCeilingUsdt, 'USDTTRC')}</>} · new address: {groupDigits(q.data.testPaymentUsdt ?? '10')} USDT test first</span>
            <span className="mt-0.5 block text-meta text-tg-hint">Used today {money(q.data.usedTodayUsdt, 'USDTTRC')} · second device on payouts {q.data.secondDevicePayouts ? 'on' : 'off'}</span>
            {src && (['dirty_hot', 'frozen'] as const).map((cls) => (
              <span key={cls} className="mt-0.5 block text-meta text-tg-hint">
                {CLASS_WORD[cls]} {money(src[cls].moveCeilingUsdt, 'USDTTRC')} / {money(src[cls].dayCeilingUsdt, 'USDTTRC')} a day · used {money(src[cls].usedTodayUsdt, 'USDTTRC')}
              </span>
            ))}
            {save.isError && (limitsChanged(save.error)
              ? <StateLine tone="bad" className="mt-0.5" more={limitsRefusal(save.error)}>Changed meanwhile — not saved. Save again.</StateLine>
              : <StateLine tone="bad" className="mt-0.5" more={limitsRefusal(save.error)}>Not saved.</StateLine>)}
            {conflict && (
              <span className="mt-0.5 block text-meta text-warn">
                Now: {conflict.capsOff ? 'No caps' : <>per move {money(conflict.moveCeilingUsdt, 'USDTTRC')} · per day {money(conflict.dayCeilingUsdt, 'USDTTRC')}</>} · second device {conflict.secondDevicePayouts ? 'on' : 'off'}
              </span>
            )}
            {save.isSuccess && !changed && <StateLine tone="good" className="mt-0.5">Saved.</StateLine>}
          </span>
          <span className="flex flex-wrap items-center gap-1.5 text-body sm:shrink-0">
            <label className="flex items-center gap-1 text-tg-hint">move {field(perMove, setPerMove, 'per-move cap, USDT')}</label>
            <label className="flex items-center gap-1 text-tg-hint">day {field(perDay, setPerDay, 'daily cap, USDT')}</label>
            {src && (
              <>
                <label className="flex items-center gap-1 text-tg-hint">{CLASS_WORD['dirty_hot']} {field(merchantMove, setMerchantMove, 'Merchant Hot per-move cap, USDT')} / {field(merchantDay, setMerchantDay, 'Merchant Hot daily cap, USDT')}</label>
                <label className="flex items-center gap-1 text-tg-hint">{CLASS_WORD['frozen']} {field(flaggedMove, setFlaggedMove, 'Flagged per-move cap, USDT')} / {field(flaggedDay, setFlaggedDay, 'Flagged daily cap, USDT')}</label>
              </>
            )}
            {/* No caps (db/0098): ticked on his word once testing ends; the 10 USDT test stays either way. */}
            <label className="flex min-h-[44px] items-center gap-1.5 text-tg-hint lg:min-h-[40px]">
              <input type="checkbox" className="h-5 w-5" checked={capsOff} disabled={save.isPending || q.isError} aria-label="no caps" onChange={(e) => setCapsOff(e.target.checked)} />
              no caps
            </label>
            {/* The switch: a payout waits for the other registered device only while this is ticked. */}
            <label className="flex min-h-[44px] items-center gap-1.5 text-tg-hint lg:min-h-[40px]">
              <input type="checkbox" className="h-5 w-5" checked={twoDevices} disabled={save.isPending || q.isError} aria-label="a payout waits for the second device" onChange={(e) => setTwoDevices(e.target.checked)} />
              second device
            </label>
            <button className="btn-secondary min-h-[44px] lg:min-h-[40px] text-body" disabled={!changed || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </span>
        </div>
      )}
    </Card>
  );
}

export function Flow() {
  const me = useQuery({ queryKey: ['custody', 'me'], queryFn: api.me });
  const keys = useQuery({ queryKey: ['custody', 'keys'], queryFn: api.keys, retry: false });
  const summary = useQuery({ queryKey: ['custody', 'summary'], queryFn: api.summary, refetchInterval: 30_000 });
  const approvals = useQuery({ queryKey: ['custody', 'approvals'], queryFn: api.approvals, refetchInterval: 15_000 });
  const whitelist = useQuery({ queryKey: ['custody', 'whitelist'], queryFn: api.whitelist });
  const addresses = useQuery({ queryKey: ['custody', 'deposit-addresses', ''], queryFn: () => api.depositAddresses(), refetchInterval: 30_000 });
  const balances = useQuery({ queryKey: ['custody', 'balances'], queryFn: api.balances, refetchInterval: 30_000 });

  const device = me.data?.device ?? null;
  const activeKeys = (keys.data?.keys ?? []).filter((k) => k.status === 'active');
  const issued = addresses.data?.addresses.length ?? 0;
  const untagged = (addresses.data?.addresses ?? []).filter((a) => !a.partySlug).length;
  const pending = approvals.data?.approvals ?? [];
  // Payable means approved in the Books BY THE OWNER (22–23 Oct 2026; lib/custody-screen.ts whitelistGroup); anything else is a proposal.
  const approved = (whitelist.data?.entries ?? []).filter((e) => whitelistGroup(e) === 'payable');
  const payStep = payStepStatus({ loading: (!whitelist.data && !whitelist.isError) || signedOut(whitelist), error: whitelist.isError && !signedOut(whitelist) ? textOf(whitelist.error) : null, payable: approved.length, hotWord: CLASS_WORD['running_hot'] ?? 'Hot' });
  const proposed = (whitelist.data?.entries ?? []).filter((e) => whitelistGroup(e) === 'awaiting');
  const hot = (balances.data?.wallets ?? []).filter((w) => w.class === 'running_hot');
  const hotTotal = hot.find((w) => w.balance !== null);
  const cable = webHid() !== null;

  const step1: Tone = device && activeKeys.length ? 'done' : 'now';
  const step2: Tone = issued > 0 ? 'done' : step1 === 'done' ? 'now' : 'todo';
  const depositsToday = summary.data?.depositsToday ?? 0;
  const step3: Tone = depositsToday > 0 ? 'done' : step2 === 'done' ? 'now' : 'todo';
  const step4: Tone = pending.length > 0 ? 'now' : step3 === 'done' ? 'done' : 'todo';
  const step5: Tone = approved.length > 0 ? 'done' : proposed.length > 0 ? 'now' : 'todo';
  const step6: Tone = approved.length > 0 ? 'now' : 'todo';
  const step7: Tone = hot.length > 0 ? 'now' : 'todo';
  const plural = (n: number, word: string) => `${n} ${n === 1 ? word : word.endsWith('s') ? `${word}es` : `${word}s`}`;

  return (
    <div className="space-y-3">
      <WindowControl />

      <Renewals />
      <Caps />
      <FeeFloors />

      <Step
        n={1} title="Register the Ledger" tone={step1}
        bad={me.isError && !signedOut(me) ? textOf(me.error) : keys.isError && !signedOut(keys) ? textOf(keys.error) : null}
        retry={() => { void me.refetch(); void keys.refetch(); }} reading={me.isFetching || keys.isFetching}
        status={signedOut(me) ? 'Waiting for sign-in.' : me.isError ? "Couldn't read your device — try again."
          : !me.data ? 'Reading…'
          : !device ? 'No signing device registered yet.'
          : signedOut(keys) ? 'Waiting for sign-in.' : keys.isError ? "Device registered. Couldn't read the keys."
          : !keys.data ? 'Device registered. Reading the keys…'
          : activeKeys.length === 0 ? 'Device registered. Register an account key by cable.'
          : `Device registered, ${plural(activeKeys.length, 'account key')} on file.`}
        more={cable ? undefined : 'Registering a key and signing by cable need Brave or Edge on the computer the Ledger is plugged into.'}
        to="/custody/keys" action="Devices & keys"
      />
      <Step
        n={2} title="New address" tone={step2}
        bad={addresses.isError && !signedOut(addresses) ? textOf(addresses.error) : null}
        retry={() => void addresses.refetch()} reading={addresses.isFetching}
        status={signedOut(addresses) ? 'Waiting for sign-in.' : addresses.isError ? "Couldn't read the addresses — try again."
          : !addresses.data ? 'Reading…'
          : issued === 0 ? 'None issued yet — press New address.'
          : `${plural(issued, 'address')} issued${untagged ? `, ${untagged} untagged` : ''}.`}
        more="One tap, here or in the wallet bot. The customer is attached when the money lands: the bot's Tag customer button, or Tag on the Addresses tab."
        to="/custody/addresses" action="Addresses"
      />
      <Step
        n={3} title="Customer pays" tone={step3}
        bad={summary.isError && !signedOut(summary) ? textOf(summary.error) : null}
        retry={() => void summary.refetch()} reading={summary.isFetching}
        status={signedOut(summary) ? 'Waiting for sign-in.' : summary.isError ? "Couldn't read the summary — try again."
          : !summary.data ? 'Reading…'
          : `${plural(depositsToday, 'deposit')} today${summary.data.openCases ? `; ${summary.data.openCases} need attention` : ''}.`}
        more="The watcher sees a deposit on its next tick; every step it takes is on the Deposits tab. The bot rings only when a deposit is held or not clean."
        to="/custody/deposits" action="Deposits"
      />
      <Step
        n={4} title="Sweep — sign on the Ledger" tone={step4}
        bad={approvals.isError && !signedOut(approvals) ? textOf(approvals.error) : null}
        retry={() => void approvals.refetch()} reading={approvals.isFetching}
        status={signedOut(approvals) ? 'Waiting for sign-in.' : approvals.isError ? "Couldn't read the approvals — try again."
          : !approvals.data ? 'Reading…'
          : pending.length === 0 ? 'Nothing waiting on you.'
          : `${plural(pending.length, 'card')} waiting for your signature.`}
        more="A swept deposit lands in Hot. The device signs for a deposit address by its path."
        to="/custody/approvals" action="Approvals"
      />
      <Step
        n={5} title="Payout address" tone={step5}
        bad={whitelist.isError && !signedOut(whitelist) ? textOf(whitelist.error) : null}
        retry={() => void whitelist.refetch()} reading={whitelist.isFetching}
        status={signedOut(whitelist) ? 'Waiting for sign-in.' : whitelist.isError ? "Couldn't read the address book — try again."
          : !whitelist.data ? 'Reading…'
          : `${plural(approved.length, 'payable address')}${proposed.length ? `, ${proposed.length} awaiting the Books` : ''}.`}
        more="An address is proposed here and approved in the Books (Approve payout address). Only that approval makes it payable; every payout still needs your Ledger."
        to="/custody/whitelist" action="Payout addresses"
      />
      {/* The Pay step reads the address book's state first (lib/custody-screen.ts): loading or failed is said as that, never as "no payable address" (Screens' Astra review, 22 Oct 2026). */}
      <Step
        n={6} title="Pay" tone={step6}
        bad={payStep.bad}
        retry={() => void whitelist.refetch()} reading={whitelist.isFetching}
        status={payStep.status}
        more="Pay on the entry, type the amount, sign the card in Approvals. The caps above bound every payout."
        to="/custody/whitelist" action="Pay"
      />
      <Step
        n={7} title="Treasury" tone={step7}
        bad={balances.isError && !signedOut(balances) ? textOf(balances.error) : null}
        retry={() => void balances.refetch()} reading={balances.isFetching}
        status={signedOut(balances) ? 'Waiting for sign-in.' : balances.isError ? "Couldn't read the balances — try again."
          : !balances.data ? 'Reading…'
          : hot.length === 0 ? `No ${CLASS_WORD['running_hot']} wallet on this chain.`
          : `${CLASS_WORD['running_hot']} holds ${hotTotal ? money(hotTotal.balance!, hotTotal.asset) : 'an unread balance'}.`}
        more="Move to Treasury from Balances; the card is a Treasury move, two of three keys once Treasury's 2-of-3 is set up."
        to="/custody/balances" action="Balances"
      />

      <MoreDetails label="About this flow">
        <p className="text-meta text-tg-hint">The whole flow, in the order the money moves. Every signature is a tap on your Ledger — by cable from this screen where the browser reaches it, from a phone otherwise — and every write needs an open window.</p>
      </MoreDetails>
    </div>
  );
}
